import { AmpWorkContext, RenderTargetContext } from '@news-mono/web-common'
import React from 'react'
import { useLocation } from 'react-router'
import { Logger } from '@west-australian-newspapers/typescript-log'
import { getLiveBlogIdFromLink, is24LiveBlog } from '../../content/Blog/Blog'
import { AmpImg } from '../../content/Picture/Image.amp'
import { AmpImage } from '../../content/Picture/Picture.amp'
import { AmpCityPulse } from '../../publication/Embed/amp/AmpCityPulse'
import { AmpFacebook } from '../../publication/Embed/amp/AmpFacebook'
import {
    AmpIFrame,
    FrameLayoutPropsFixedHeight,
    FrameLayoutPropsFixedResponsive,
} from '../../publication/Embed/amp/AmpIFrame'
import { AmpInstagram } from '../../publication/Embed/amp/AmpInstagram'
import { AmpOmny } from '../../publication/Embed/amp/AmpOmny'
import { AmpShortstack } from '../../publication/Embed/amp/AmpShortstack'
import { StyledContent } from '../../publication/Embed/amp/AmpStyledContent.styled'
import { AmpTwitter } from '../../publication/Embed/amp/AmpTwitter'
import { AmpYoutube } from '../../publication/Embed/amp/AmpYoutube'
import { EmbedProps } from '../../publication/Embed/Embed'
import { AmpACast } from './amp/AmpACast'
import { AmpDatamapper, isAmpDatamapper } from './amp/AmpDatamapper'
import { AmpFlourish } from './amp/AmpFlourish'
import { AmpLiveBlogEmbed } from './amp/AmpLiveBlog'
import { AmpSlido } from './amp/AmpSlido'
import { AmpSurveyMonkey } from './amp/AmpSurveyMonkey'
import { AmpWayIn } from './amp/AmpWayIn'
import { AmpWhistleOut } from './amp/AmpWhistleOut'
import { StyledEmbedlyFallback } from './Embed.styled'
import { isACast } from './helpers/acast'
import { getEmbedId, loadEmbedlyData } from './helpers/embedly'
import { isFacebook } from './helpers/facebook'
import { isDeprecatedFanhubEmbed } from './helpers/fanhub'
import { isFlourish } from './helpers/flourish'
import { isInstagram, parseInstagramLink } from './helpers/instagram'
import { isOurLegacyLiveBlog } from './helpers/liveevent'
import { isNewsTipsForm } from './helpers/newsTipsForm'
import { getSpotifyEmbedHeight } from './helpers/spotify'
import { isTwitterUrl, tweetIDFromURl } from './helpers/twitter'
import { isWhistleOut } from './helpers/whistleout'
import { isSurveyMonkey } from './web/SurveyMonkeyJS'
import { isWayIn } from './web/WayIn'

export interface AmpEmbedlyProps {
    /** setting height override will mark layout as fixed-height and width as undefined as that is what the AmpIFrame requires */
    heightOverride?: number
    link: string
    apiKey: string
    width: number
    layout: 'responsive' | 'fixed'
    log: Logger
}

/* https://stackoverflow.com/questions/3452546/how-do-i-get-the-youtube-video-id-from-a-url */
export const youtubeVideoId = (link: string) => {
    const regExp =
        /^.*(?:(?:youtu.be\/)|(?:v\/)|(?:\/u\/\w\/)|(?:embed\/)|(?:watch\?))\??v?=?([^#&?]*).*/
    const match = link.match(regExp)
    if (match && !!match[1]) {
        const videoId = match[1]
        if (videoId.length === 11) {
            return videoId
        }
    }
    return undefined
}

export class AmpEmbedly extends React.Component<AmpEmbedlyProps> {
    static contextType = RenderTargetContext
    context!: AmpWorkContext
    logger: Logger

    constructor(props: AmpEmbedlyProps, context: AmpWorkContext) {
        super(props)

        const { link, log } = props
        this.logger = log

        if (!context.getWorkResult(this.getKey())) {
            context.registerWork(this.getKey(), loadEmbedlyData(link, log))
        }
    }

    getKey() {
        return getEmbedId(this.props.link)
    }

    render() {
        this.logger.info(
            { embedlyLink: this.props.link },
            'Calling Embedly from AMP context',
        )
        const { link, heightOverride, layout } = this.props
        const oEmbedResponse = this.context.getWorkResult(this.getKey())
        if (!oEmbedResponse) {
            return (
                <StyledContent>
                    <a href={link}>{link}</a>
                </StyledContent>
            )
        }

        const iframe = oEmbedResponse.html
        let thumbnailUrl = oEmbedResponse.thumbnail_url
        if (!thumbnailUrl) {
            thumbnailUrl =
                'https://cdn.embed.ly/logos/embedly-powered-large-light.png'
        }
        const match = /src="([^"]+)"/.exec(iframe)

        const height = heightOverride || oEmbedResponse.height

        const dimensions: (
            | FrameLayoutPropsFixedResponsive
            | FrameLayoutPropsFixedHeight
        ) & {
            height: number
        } = heightOverride
            ? {
                  layout: 'fixed-height',
                  width: undefined,
                  height: heightOverride,
              }
            : {
                  layout,
                  width: oEmbedResponse.width,
                  height,
              }

        if (match && match.length === 2) {
            const src = match[1]
            return (
                <StyledContent>
                    <AmpIFrame
                        {...dimensions}
                        allowSameOrigin={true}
                        allowPopups={true}
                        allowForms={true}
                        src={src}
                        frameborder={0}
                    >
                        {thumbnailUrl && (
                            <AmpImg
                                src={thumbnailUrl}
                                width={oEmbedResponse.width}
                                height={height}
                                layout="fill"
                                placeholder=""
                            />
                        )}
                    </AmpIFrame>
                </StyledContent>
            )
        }

        // Fallback display the source, provider name, title, desc and thumbnail if possible
        const url = oEmbedResponse.url
        const providerName = oEmbedResponse.provider_name
        const providerUrl = oEmbedResponse.provider_url
        const { title, description } = oEmbedResponse
        const thumbWidth = oEmbedResponse.thumbnail_width
        const thumbHeight = oEmbedResponse.thumbnail_height
        const thumbUrl = oEmbedResponse.thumbnail_url

        if (url && providerName && title && description) {
            return (
                <StyledEmbedlyFallback>
                    <div>
                        <span>
                            <strong>{providerName}</strong>
                        </span>
                        {` | `}
                        <span>{title}</span>
                    </div>
                    <div>
                        <span className="embed-fallback-desc">
                            {description}
                        </span>
                    </div>
                    {thumbUrl && thumbWidth && thumbHeight ? (
                        <React.Fragment>
                            <div>
                                <a href={url}>
                                    <AmpImage
                                        src={thumbUrl}
                                        width={thumbWidth}
                                        height={thumbHeight}
                                    />
                                </a>
                            </div>
                            <a
                                className="embed-fallback-url"
                                href={url}
                            >{`View on ${providerUrl}`}</a>
                        </React.Fragment>
                    ) : (
                        <a href={url}>{url}</a> // fallback show url
                    )}
                </StyledEmbedlyFallback>
            )
        }

        // Dont do anything as its not a valid embedly response
        return null
    }
}

const EMBEDLY_API_KEY = '2ac17161f707456d853e010bafc56489'

export const AmpEmbed: React.FC<EmbedProps> = ({ link, log, onEvent }) => {
    const location = useLocation()
    // This uses google amp native twitter and facebook embeds
    // then falls back to embedly
    const height = 250
    const width = 300

    if (isNewsTipsForm(link)) {
        return null
    }

    if (isSurveyMonkey(link)) {
        return <AmpSurveyMonkey />
    }
    if (isTwitterUrl(link)) {
        const tweetId = tweetIDFromURl(link)
        if (tweetId) {
            return (
                <AmpTwitter width={width} height={height} tweetId={tweetId} />
            )
        }
    } else if (isFacebook(link)) {
        return <AmpFacebook width={300} height={250} url={link} />
    } else if (isInstagram(link)) {
        const { shortCode } = parseInstagramLink(link)
        if (shortCode) {
            return <AmpInstagram size={width} shortCode={shortCode} />
        }
    } else if (link.indexOf('youtube') >= 0 || link.indexOf('youtu.be') >= 0) {
        const videoId = youtubeVideoId(link)
        if (videoId !== undefined) {
            return (
                <AmpYoutube width={width} height={height} videoId={videoId} />
            )
        }
    } else if (link.indexOf('omny.fm') >= 0) {
        return <AmpOmny link={link} height={180} frameborder={0} />
    } else if (link.indexOf('download.pwc') >= 0) {
        return <AmpCityPulse link={link} height={650} frameborder={0} />
    } else if (link.indexOf('shortstack') >= 0) {
        return <AmpShortstack link={link} onEvent={onEvent} />
    } else if (is24LiveBlog(link)) {
        const liveBlogId = getLiveBlogIdFromLink(link)
        if (liveBlogId) {
            return <AmpLiveBlogEmbed id={liveBlogId} />
        }
    } else if (isOurLegacyLiveBlog(link) || isDeprecatedFanhubEmbed(link)) {
        return null
    } else if (isWayIn(link)) {
        return (
            <AmpWayIn link={link} height={400} width={width} resizable={true} />
        )
    } else if (isWhistleOut(link)) {
        return <AmpWhistleOut link={link} />
    } else if (isACast(link)) {
        return <AmpACast link={link} />
    } else if (link.indexOf('spotify.com') >= 0) {
        // Run embedly with a height override for spotify

        const spotifyHeight = getSpotifyEmbedHeight(link)

        return (
            <AmpEmbedly
                link={link}
                width={width}
                heightOverride={spotifyHeight || 300}
                layout={'fixed'}
                apiKey={EMBEDLY_API_KEY}
                log={log}
            />
        )
    } else if (isFlourish(link)) {
        return <AmpFlourish link={link} />
    } else if (link.indexOf('app.sli.do') >= 0) {
        return <AmpSlido link={link} height={560} frameborder={0} />
    } else if (isAmpDatamapper(link)) {
        return <AmpDatamapper link={link} />
    }

    return (
        <AmpEmbedly
            link={link}
            width={width}
            layout={'responsive'}
            apiKey={EMBEDLY_API_KEY}
            log={log}
        />
    )
}

export default AmpEmbed
